import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsResponse } from '@cloudsign/shared';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { CAPABILITIES_KEY } from '../lib/session';
import { diffValues } from '../lib/settingsSchema';
import { Button, Spinner } from './ui';
import { useToastOnSuccess } from './Toast';
import { ErrorLine, ErrorPanel, fieldErrors } from './ErrorPanel';
import { SettingsForm } from './SettingsForm';

/**
 * Loads one module's settings, renders the generic form and saves only what changed.
 * `above` can show extra content (the branding preview) that follows the unsaved values.
 */
export function SettingsEditor({
  moduleKey,
  base,
  canEdit,
  above,
  groups,
  exceptGroups,
}: {
  moduleKey: string;
  /** '/settings' for a tenant, '/platform/settings' for the platform, '/sites/<id>/settings' or '/groups/<id>/settings' for a scope. */
  base: string;
  canEdit: boolean;
  above?: (values: Record<string, unknown>) => ReactNode;
  /** Show only these top-level setting groups (default: all). */
  groups?: readonly string[];
  /** Hide these top-level setting groups. */
  exceptGroups?: readonly string[];
}) {
  const qc = useQueryClient();
  const key = ['settings', base, moduleKey];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`${base}/${moduleKey}`, { schema: SettingsResponse }) });
  const [values, setValues] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (q.data) setValues(q.data.values);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => api.patch(`${base}/${moduleKey}`, { body: diffValues(q.data?.values, values), schema: SettingsResponse }),
    onSuccess: async (r) => {
      qc.setQueryData(key, r);
      setValues(r.values);
      // Branding and security settings feed the shell, so refresh it.
      await qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
    },
  });

  useToastOnSuccess(save.isSuccess, t('common.saved'));
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const data = q.data;
  if (!data || !values) return <Spinner />;
  const changed = Object.keys(diffValues(data.values, values)).length > 0;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const footer = canEdit ? (
    <>
      <Button disabled={!changed} onClick={() => setValues(q.data.values)}>
        {t('settings.discard')}
      </Button>
      <Button type="submit" variant="primary" disabled={save.isPending || !changed}>
        {t('common.save')}
      </Button>
    </>
  ) : undefined;
  return (
    <form onSubmit={submit} className="space-y-6">
      {above ? above(values) : null}
      <ErrorLine error={save.error} />
      <SettingsForm
        moduleKey={moduleKey}
        schema={data.jsonSchema}
        values={values}
        defaults={data.defaults}
        disabled={!canEdit}
        errors={fieldErrors(save.error)}
        footer={footer}
        groups={groups}
        exceptGroups={exceptGroups}
        onChange={(v) => {
          save.reset();
          setValues(v);
        }}
      />
    </form>
  );
}
